import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import {
  auditoriaDestacamento, destacamentosList, personalList,
  type AuditAccion, type AuditPresencia, type Destacamento, type Personal,
} from '../api/seguridad';

const ACCION_LABEL: Record<string, string> = {
  creada: '🆕 Creada', push_enviado: '📡 Despachada', contacto_aviado: '📞 Aviso a contacto',
  asignada: '📍 Asignada', en_atencion: '🚔 Tomada', nota: '📝 Nota', reubicada: '🗺 Reubicada',
  atendida: '✅ Atendida', falsa: '⚠️ Falsa', sin_respuesta: '🔇 Sin respuesta', cancelada: '❌ Cancelada',
};

function fecha(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso.replace(' ', 'T')).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** Minutos entre dos timestamps ISO (b - a). */
function minutosEntre(a: string, b: string): number {
  return (new Date(b.replace(' ', 'T')).getTime() - new Date(a.replace(' ', 'T')).getTime()) / 60000;
}

/** Etiqueta legible de una duración en minutos. */
function durLabel(min: number | null): string {
  if (min == null) return '—';
  const t = Math.round(min);
  if (t < 60) return `${t} min`;
  const h = Math.floor(t / 60), m = t % 60;
  return `${h}h ${m}m`;
}

/** Escapa un valor para CSV (separador ; — estándar Excel-AR). */
function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Genera y descarga un CSV (BOM UTF-8 para que Excel abra bien los acentos). */
function descargarCSV(nombre: string, filas: (string | number | null)[][]) {
  const cuerpo = filas.map((f) => f.map(csvCell).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + cuerpo], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function escHtml(s: unknown): string {
  return String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

export default function Auditoria() {
  const user = useAuth((s) => s.user);
  const nav = useNavigate();

  const [destId, setDestId] = useState<number | null>(user?.destacamento_id ?? null);
  const [destacamentos, setDestacamentos] = useState<Destacamento[]>([]);
  const [acciones, setAcciones] = useState<AuditAccion[]>([]);
  const [presencia, setPresencia] = useState<AuditPresencia[]>([]);
  const [tab, setTab] = useState<'acciones' | 'presencia'>('acciones');
  const [err, setErr] = useState<string | null>(null);

  // Filtros de servidor (recargan)
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [personaId, setPersonaId] = useState<number>(0);   // admin_usuario_id
  const [personas, setPersonas] = useState<Personal[]>([]);
  const [cargando, setCargando] = useState(false);

  // Filtros de cliente (solo acciones)
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [zonaFiltro, setZonaFiltro] = useState('');

  useEffect(() => { if (!destId) destacamentosList().then(setDestacamentos).catch(() => {}); }, [destId]);

  useEffect(() => {
    if (!destId) { setPersonas([]); return; }
    personalList(destId)
      .then((ps) => setPersonas(ps.filter((p) => p.admin_usuario_id)))
      .catch(() => {});
  }, [destId]);

  const cargar = useCallback(async () => {
    if (!destId) return;
    setCargando(true);
    try {
      const d = await auditoriaDestacamento(destId, {
        desde: desde || undefined,
        hasta: hasta || undefined,
        adminUsuarioId: personaId || undefined,
        limit: 2000,
      });
      setAcciones(d.acciones); setPresencia(d.presencia); setErr(null);
    } catch (e: any) { setErr(e?.message || 'Error al cargar la auditoría'); }
    finally { setCargando(false); }
  }, [destId, desde, hasta, personaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const destNombre = user?.destacamento_nombre ?? destacamentos.find((d) => d.id === destId)?.nombre ?? 'Comisaría';

  const nombrePersona = useMemo(() => {
    const p = personas.find((x) => x.admin_usuario_id === personaId);
    return p ? `${p.apellido}-${p.nombre}` : '';
  }, [personas, personaId]);

  // tipos / zonas presentes para poblar los selectores
  const tiposDisp = useMemo(() => [...new Set(acciones.map((a) => a.tipo))], [acciones]);
  const zonasDisp = useMemo(() => [...new Set(acciones.map((a) => a.zona_nombre ?? 's/zona'))], [acciones]);

  const accionesFiltradas = useMemo(() => acciones.filter((a) =>
    (!tipoFiltro || a.tipo === tipoFiltro) &&
    (!zonaFiltro || (a.zona_nombre ?? 's/zona') === zonaFiltro)
  ), [acciones, tipoFiltro, zonaFiltro]);

  // Mapa alerta_id → momento de creación (para tiempo de respuesta)
  const creadaDe = useMemo(() => {
    const m: Record<number, string> = {};
    acciones.forEach((a) => { if (a.tipo === 'creada') m[a.alerta_id] = a.creado_en; });
    return m;
  }, [acciones]);

  // Resumen del período (sobre acciones filtradas por fecha/persona)
  const resumen = useMemo(() => {
    const tomadas = acciones.filter((a) => a.tipo === 'en_atencion');
    const tiempos: number[] = [];
    tomadas.forEach((a) => {
      const c = creadaDe[a.alerta_id];
      if (c) { const min = minutosEntre(c, a.creado_en); if (min >= 0) tiempos.push(min); }
    });
    const porAgente: Record<string, number> = {};
    tomadas.forEach((a) => { const q = a.quien ?? '—'; porAgente[q] = (porAgente[q] || 0) + 1; });
    return {
      alertas: new Set(acciones.map((a) => a.alerta_id)).size,
      tomadas: tomadas.length,
      atendidas: acciones.filter((a) => a.tipo === 'atendida').length,
      falsas: acciones.filter((a) => a.tipo === 'falsa').length,
      promMin: tiempos.length ? tiempos.reduce((x, y) => x + y, 0) / tiempos.length : null,
      porAgente: Object.entries(porAgente).sort((a, b) => b[1] - a[1]),
    };
  }, [acciones, creadaDe]);

  const sufijo = useMemo(() => {
    const partes = [desde && `desde-${desde}`, hasta && `hasta-${hasta}`, nombrePersona,
      tipoFiltro && `tipo-${tipoFiltro}`].filter(Boolean);
    return partes.length ? '_' + partes.join('_') : '';
  }, [desde, hasta, nombrePersona, tipoFiltro]);

  /** Tiempo de respuesta (min) para una acción "tomada", si se puede calcular. */
  function tiempoResp(a: AuditAccion): number | '' {
    if (a.tipo !== 'en_atencion') return '';
    const c = creadaDe[a.alerta_id];
    if (!c) return '';
    const m = minutosEntre(c, a.creado_en);
    return m >= 0 ? Math.round(m) : '';
  }

  function exportarCSV() {
    if (tab === 'acciones') {
      const filas: (string | number | null)[][] = [
        ['Fecha', 'Acción', 'Alerta', 'Tipo alerta', 'Zona', 'Quién', 'Detalle', 'Latitud', 'Longitud', 'Tiempo resp. (min)'],
        ...accionesFiltradas.map((a) => [
          a.creado_en, ACCION_LABEL[a.tipo] ?? a.tipo, a.alerta_id, a.alerta_tipo,
          a.zona_nombre ?? '', a.quien ?? '', a.detalle ?? '',
          a.latitud_agente ?? '', a.longitud_agente ?? '', tiempoResp(a),
        ]),
      ];
      descargarCSV(`auditoria_acciones${sufijo}.csv`, filas);
    } else {
      const filas: (string | number | null)[][] = [
        ['Quién', 'Turno', 'Rol', 'Ingreso', 'Egreso'],
        ...presencia.map((p) => [p.quien ?? '', p.turno_tipo, p.rol_turno, p.ingreso_en ?? '', p.egreso_en ?? '']),
      ];
      descargarCSV(`auditoria_presencia${sufijo}.csv`, filas);
    }
  }

  function exportarPDF() {
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { alert('Habilitá las ventanas emergentes para exportar el PDF.'); return; }
    const rango = [desde && `desde ${desde}`, hasta && `hasta ${hasta}`].filter(Boolean).join(' ') || 'todo el período';
    const persona = nombrePersona ? nombrePersona.replace('-', ', ') : 'todo el personal';
    const esAcc = tab === 'acciones';
    const titulo = esAcc ? 'Acciones sobre alertas' : 'Presencia de guardia';

    const resumenHtml = esAcc ? `
      <div class="cards">
        <div class="card"><span>${resumen.alertas}</span>Alertas</div>
        <div class="card"><span>${resumen.tomadas}</span>Tomadas</div>
        <div class="card"><span>${resumen.atendidas}</span>Atendidas</div>
        <div class="card"><span>${resumen.falsas}</span>Falsas</div>
        <div class="card"><span>${durLabel(resumen.promMin)}</span>Resp. promedio</div>
      </div>
      ${resumen.porAgente.length ? `<p class="sub">Tomadas por agente: ${resumen.porAgente.map(([q, n]) => `${escHtml(q)} (${n})`).join(' · ')}</p>` : ''}
    ` : '';

    const filasHtml = esAcc
      ? accionesFiltradas.map((a) => {
          const tr = tiempoResp(a);
          return `<tr><td>${escHtml(fecha(a.creado_en))}</td><td>${escHtml(ACCION_LABEL[a.tipo] ?? a.tipo)}</td>
            <td>#${a.alerta_id}</td><td>${escHtml(a.zona_nombre ?? 's/zona')}</td>
            <td>${escHtml(a.quien ?? '—')}</td><td>${tr === '' ? '' : tr + ' min'}</td>
            <td>${escHtml(a.detalle ?? '')}</td></tr>`;
        }).join('')
      : presencia.map((p) => `<tr><td>${escHtml(p.quien ?? '—')}</td><td>${escHtml(p.turno_tipo)}</td>
          <td>${escHtml(p.rol_turno)}</td><td>${escHtml(fecha(p.ingreso_en))}</td>
          <td>${p.egreso_en ? escHtml(fecha(p.egreso_en)) : 'en servicio'}</td></tr>`).join('');

    const encabezado = esAcc
      ? '<tr><th>Fecha</th><th>Acción</th><th>Alerta</th><th>Zona</th><th>Quién</th><th>Resp.</th><th>Detalle</th></tr>'
      : '<tr><th>Quién</th><th>Turno</th><th>Rol</th><th>Ingreso</th><th>Egreso</th></tr>';

    const nFilas = esAcc ? accionesFiltradas.length : presencia.length;

    w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8">
      <title>Auditoría — ${escHtml(destNombre)}</title>
      <style>
        *{box-sizing:border-box;font-family:system-ui,Segoe UI,Arial,sans-serif}
        body{margin:28px;color:#1e293b;font-size:12px}
        .membrete{display:flex;align-items:center;gap:12px;border-bottom:3px solid #2563eb;padding-bottom:10px;margin-bottom:14px}
        .escudo{font-size:34px}
        .membrete h1{margin:0;font-size:18px;color:#1d4ed8}
        .membrete p{margin:2px 0 0;color:#64748b;font-size:12px}
        .meta{color:#475569;font-size:12px;margin:0 0 12px}
        .meta b{color:#1e293b}
        .cards{display:flex;gap:8px;flex-wrap:wrap;margin:10px 0}
        .card{flex:1;min-width:90px;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:8px;padding:8px;text-align:center}
        .card span{display:block;font-size:20px;font-weight:700;color:#1d4ed8}
        .sub{color:#475569;font-size:11px;margin:4px 0 12px}
        table{width:100%;border-collapse:collapse;margin-top:8px}
        th,td{border:1px solid #e2e8f0;padding:5px 7px;text-align:left;vertical-align:top}
        th{background:#eff6ff;color:#1e40af;font-size:11px}
        tr:nth-child(even) td{background:#f8fafc}
        .pie{margin-top:16px;color:#94a3b8;font-size:10px;text-align:right}
        @media print{.noprint{display:none}}
      </style></head><body>
      <div class="membrete">
        <div class="escudo">🛡️</div>
        <div><h1>Informe de auditoría</h1><p>${escHtml(destNombre)} — ${escHtml(titulo)}</p></div>
      </div>
      <p class="meta">Período: <b>${escHtml(rango)}</b> · Personal: <b>${escHtml(persona)}</b> · Registros: <b>${nFilas}</b></p>
      ${resumenHtml}
      <table><thead>${encabezado}</thead><tbody>${filasHtml || '<tr><td colspan="7" style="text-align:center;color:#94a3b8">Sin registros.</td></tr>'}</tbody></table>
      <p class="pie">Generado desde CiudadApp Seguridad · ${escHtml(new Date().toLocaleString('es-AR'))}</p>
      <script>window.onload=function(){setTimeout(function(){window.print()},250)}<\/script>
      </body></html>`);
    w.document.close();
    w.focus();
  }

  function limpiar() { setDesde(''); setHasta(''); setPersonaId(0); setTipoFiltro(''); setZonaFiltro(''); }

  if (!user) return null;

  const hayFiltro = !!(desde || hasta || personaId || tipoFiltro || zonaFiltro);
  const filasActuales = tab === 'acciones' ? accionesFiltradas.length : presencia.length;

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 shrink-0">
        <button onClick={() => nav('/')} className="text-slate-500 hover:text-slate-900 text-xl leading-none">←</button>
        <div className="flex-1">
          <h1 className="text-slate-900 font-bold leading-tight">Auditoría</h1>
          <p className="text-slate-500 text-xs leading-tight">{destNombre}</p>
        </div>
      </header>

      {err && <div className="bg-red-50 border-b border-red-200 text-red-700 text-xs px-4 py-1.5">{err}</div>}

      {!destId && (
        <div className="p-4 max-w-md">
          <label className="block text-sm font-semibold text-slate-700 mb-1">Elegí un destacamento</label>
          <select onChange={(e) => setDestId(Number(e.target.value) || null)} className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2.5 text-sm">
            <option value="">— seleccioná —</option>
            {destacamentos.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </div>
      )}

      {destId && (
        <>
          <div className="flex gap-1 p-2 bg-white border-b border-slate-200 max-w-2xl w-full mx-auto">
            <button onClick={() => setTab('acciones')} className={`flex-1 text-xs font-semibold rounded-lg py-2 ${tab === 'acciones' ? 'bg-brand-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>Acciones ({accionesFiltradas.length})</button>
            <button onClick={() => setTab('presencia')} className={`flex-1 text-xs font-semibold rounded-lg py-2 ${tab === 'presencia' ? 'bg-brand-500 text-white' : 'text-slate-500 hover:bg-slate-100'}`}>Presencia ({presencia.length})</button>
          </div>

          {/* Resumen del período (solo acciones) */}
          {tab === 'acciones' && (
            <div className="max-w-2xl w-full mx-auto px-2 pt-2">
              <div className="grid grid-cols-5 gap-1.5">
                {[
                  { n: resumen.alertas, l: 'Alertas' },
                  { n: resumen.tomadas, l: 'Tomadas' },
                  { n: resumen.atendidas, l: 'Atendidas' },
                  { n: resumen.falsas, l: 'Falsas' },
                  { n: durLabel(resumen.promMin), l: 'Resp. prom.' },
                ].map((c) => (
                  <div key={c.l} className="bg-white border border-slate-200 rounded-lg py-1.5 text-center">
                    <div className="text-brand-700 font-bold text-sm leading-none">{c.n}</div>
                    <div className="text-slate-400 text-[9px] uppercase mt-0.5">{c.l}</div>
                  </div>
                ))}
              </div>
              {resumen.porAgente.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Tomadas por agente: {resumen.porAgente.map(([q, n]) => `${q} (${n})`).join(' · ')}
                </p>
              )}
            </div>
          )}

          {/* Filtros + exportar */}
          <div className="bg-white border-b border-slate-200 px-2 py-2 max-w-2xl w-full mx-auto mt-2">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Desde</label>
                <input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)}
                  className="bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs" />
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Hasta</label>
                <input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)}
                  className="bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs" />
              </div>
              <div className="min-w-[8rem] flex-1">
                <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Persona</label>
                <select value={personaId} onChange={(e) => setPersonaId(Number(e.target.value))}
                  className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs">
                  <option value={0}>Todas</option>
                  {personas.map((p) => <option key={p.id} value={p.admin_usuario_id!}>{p.apellido}, {p.nombre}</option>)}
                </select>
              </div>
              {tab === 'acciones' && (
                <>
                  <div className="min-w-[8rem]">
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Tipo</label>
                    <select value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs">
                      <option value="">Todos</option>
                      {tiposDisp.map((t) => <option key={t} value={t}>{ACCION_LABEL[t] ?? t}</option>)}
                    </select>
                  </div>
                  <div className="min-w-[8rem]">
                    <label className="block text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Zona</label>
                    <select value={zonaFiltro} onChange={(e) => setZonaFiltro(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs">
                      <option value="">Todas</option>
                      {zonasDisp.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                  </div>
                </>
              )}
              {hayFiltro && (
                <button onClick={limpiar} className="text-xs text-slate-500 hover:text-slate-800 underline px-1 py-1.5">Limpiar</button>
              )}
              <div className="ml-auto flex gap-1.5">
                <button onClick={exportarPDF} disabled={cargando || filasActuales === 0}
                  className="bg-slate-700 hover:bg-slate-800 disabled:opacity-40 text-white text-xs font-semibold rounded-lg px-3 py-1.5">
                  🖨 PDF
                </button>
                <button onClick={exportarCSV} disabled={cargando || filasActuales === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-xs font-semibold rounded-lg px-3 py-1.5">
                  ⤓ CSV
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              {cargando ? 'Cargando…' : `${filasActuales} registro${filasActuales === 1 ? '' : 's'} en "${tab === 'acciones' ? 'Acciones' : 'Presencia'}"${hayFiltro ? ' (filtrado)' : ''}`}
            </p>
          </div>

          <main className="flex-1 overflow-y-auto p-3 max-w-2xl w-full mx-auto">
            {tab === 'acciones' && (
              <div className="space-y-1.5">
                {accionesFiltradas.length === 0 && <p className="text-slate-400 text-sm text-center py-8">Sin registros.</p>}
                {accionesFiltradas.map((a) => {
                  const gps = a.latitud_agente && a.longitud_agente
                    ? `https://www.google.com/maps?q=${a.latitud_agente},${a.longitud_agente}` : null;
                  const tr = tiempoResp(a);
                  return (
                    <div key={a.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-slate-800">{ACCION_LABEL[a.tipo] ?? a.tipo}</span>
                        <span className="text-slate-400 text-[11px]">{fecha(a.creado_en)}</span>
                      </div>
                      <p className="text-slate-500 text-xs">
                        Alerta #{a.alerta_id} · {a.zona_nombre ?? 's/zona'}
                        {a.quien && <> · por <b className="text-slate-700">{a.quien}</b></>}
                        {tr !== '' && <> · ⏱ {tr} min</>}
                        {gps && <> · <a className="text-brand-600" href={gps} target="_blank" rel="noopener noreferrer">📍 GPS</a></>}
                      </p>
                      {a.detalle && <p className="text-slate-500 text-xs italic">{a.detalle}</p>}
                    </div>
                  );
                })}
              </div>
            )}

            {tab === 'presencia' && (
              <div className="space-y-1.5">
                {presencia.length === 0 && <p className="text-slate-400 text-sm text-center py-8">Sin registros de guardia.</p>}
                {presencia.map((p) => (
                  <div key={p.id} className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-slate-800 text-sm font-medium truncate">{p.quien ?? '—'}</p>
                      <p className="text-slate-500 text-xs">Turno {p.turno_tipo} · {p.rol_turno}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-emerald-700 text-[11px]">▸ {fecha(p.ingreso_en)}</p>
                      <p className="text-slate-400 text-[11px]">{p.egreso_en ? `◂ ${fecha(p.egreso_en)}` : 'en servicio'}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </>
      )}
    </div>
  );
}
